import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Users } from 'lucide-react';
import '../../styles/contents/shortFormQuiz/ShortFormQuizHomePage.css';
import { useAuth } from '../../shared/contexts/AuthContext';
import { db, checkFirebaseConnection, safeFirestoreRead } from '../../shared/api/firebase';
import { doc } from 'firebase/firestore';
import { getTeacherClasses, getAllStudentNames } from '../../services/firebaseService';
import QuizCard from '../../components/shortFormQuiz/QuizCard';
import AddQuizCard from '../../components/shortFormQuiz/AddQuizCard';
import { deleteShortFormQuiz } from './ShortFormQuizService';

// 헬퍼 함수들 (ShortFormQuizService와 동일)
const getCollectionName = (currentUser) => {
  return currentUser ? 'shortForm_Quiz_member' : 'shortForm_Quiz_guest';
};

const generateUserId = (currentUser) => {
  if (currentUser) return currentUser.uid;
  
  const guestIdKey = 'guest_shortform_id';
  const existingGuestId = localStorage.getItem(guestIdKey);
  if (existingGuestId) return existingGuestId;

  const timestamp = new Date().getTime();
  const randomStr = Math.random().toString(36).substring(2, 10);
  const newGuestId = `guest_${timestamp}_${randomStr}`;
  
  localStorage.setItem(guestIdKey, newGuestId);
  return newGuestId;
};

const getStorageKey = (userId = null, classId = null) => {
  const id = userId || 'guest';
  const baseKey = userId && !userId.startsWith('guest_') ? `shortForm_savedQuizzes_${id}` : 'shortForm_savedQuizzes_guest';
  return classId ? `${baseKey}_${classId}` : baseKey;
};

// localStorage 데이터 정규화 함수 (TeacherTest 방식)
const normalizeLocalStorageData = (storageKey) => {
  try {
    const data = localStorage.getItem(storageKey);
    if (!data) return [];
    
    const parsed = JSON.parse(data);
    
    // 이미 배열인 경우
    if (Array.isArray(parsed)) {
      return parsed.map(quiz => {
        // 데이터 정규화
        if (!quiz.accessCode) quiz.accessCode = quiz.id;
        if (!quiz.meta) {
          quiz.meta = {
            grade: quiz.grade,
            subject: quiz.subject,
            school: quiz.school,
            mode: quiz.mode,
            topic: quiz.topic
          };
        }
        return quiz;
      });
    }
    
    // 객체인 경우 - Firestore 구조에서 배열 추출
    if (typeof parsed === 'object' && parsed !== null) {
      if (parsed.퀴즈List && Array.isArray(parsed.퀴즈List)) {
        // 정규화된 배열로 저장
        const normalizedQuizzes = parsed.퀴즈List.map(quiz => {
          if (!quiz.accessCode) quiz.accessCode = quiz.id;
          if (!quiz.meta) {
            quiz.meta = {
              grade: quiz.grade,
              subject: quiz.subject,
              school: quiz.school,
              mode: quiz.mode,
              topic: quiz.topic
            };
          }
          return quiz;
        });
        localStorage.setItem(storageKey, JSON.stringify(normalizedQuizzes));
        return normalizedQuizzes;
      }
    }
    
    // 예상치 못한 형태 - 초기화
    localStorage.removeItem(storageKey);
    return [];
  } catch (error) {
    try {
      localStorage.removeItem(storageKey);
    } catch (removeError) {
      // 제거도 실패한 경우 무시
    }
    return [];
  }
};

const ShortFormQuizHomePage = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const { currentUser, loading: authLoading, isGuest } = useAuth();
  const [savedQuizzes, setSavedQuizzes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [userId, setUserId] = useState('');
  const [hasInitialLoad, setHasInitialLoad] = useState(false); // 최초 로딩 완료 여부

  // localStorage에서 클래스 정보 가져오기
  const getStoredClassData = () => {
    try {
      // 다른 페이지들과 일관성을 위해 shortFormQuiz_selectedClass 키 사용
      const classInfo = localStorage.getItem('shortFormQuiz_selectedClass') || 
                       localStorage.getItem('classInfo'); // 기존 호환성 유지
      return classInfo ? JSON.parse(classInfo) : null;
    } catch (error) {
      return null;
    }
  };

  const [storedClassData] = useState(getStoredClassData());
  const [classInfo, setClassInfo] = useState(null);
  const [classLoadError, setClassLoadError] = useState(null);
  const [classStudentCount, setClassStudentCount] = useState(0); // 학급 학생 수

  // localStorage에서 학급 정보 로드
  useEffect(() => {
    const loadClassData = async () => {
      try {
        if (storedClassData) {
          const { classId, currentUserInfo } = storedClassData;
          
          // 비회원이거나 guest 클래스인 경우
          if (!currentUserInfo?.uid || currentUserInfo?.isGuest || classId?.startsWith('guest_')) {
            setClassInfo({
              className: '새 학급으로 시작하기',
              classId: classId
            });
            setClassStudentCount(0);
          } else {
            // 회원인 경우 실제 학급 정보 로드
            try {
              const classesResult = await getTeacherClasses(currentUserInfo.uid);
              
              if (classesResult.success) {
                const selectedClass = classesResult.data.find(cls => cls.id === classId);
                
                if (selectedClass) {
                  setClassInfo({
                    ...selectedClass,
                    classId: classId
                  });
                  
                  // 학급 학생 수 로드
                  try {
                    const studentsResult = await getAllStudentNames(classId);
                    if (studentsResult.success && studentsResult.data.length > 0) {
                      setClassStudentCount(studentsResult.data.length);
                    } else {
                      setClassStudentCount(0);
                    }
                  } catch (studentError) {
                    setClassStudentCount(0);
                  }
                } else {
                  throw new Error('선택된 학급을 찾을 수 없습니다.');
                }
              } else {
                throw new Error(classesResult.error || '학급 목록을 불러올 수 없습니다.');
              }
            } catch (error) {
              setClassLoadError(error.message);
              setClassInfo({
                className: '임시 학급',
                classId: classId
              });
              setClassStudentCount(0);
            }
          }
        }
      } catch (error) {
        setClassLoadError('저장된 학급 정보를 불러올 수 없습니다.');
      }
    };
    
    loadClassData();
  }, [storedClassData]);

  // 🚀 완전히 새로운 데이터 로딩 함수 - TeacherTest 방식 적용
  const loadQuizzes = useCallback(async (currentUserId, forceRefresh = false) => {
    try {
      
      // userId 검증
      if (!currentUserId || currentUserId.trim() === '') {
        setError('사용자 ID가 유효하지 않습니다.');
        setIsLoading(false);
        return;
      }
      
      // 현재 상태에서 classInfo 참조 (의존성 제거를 위해)
      const currentClassId = classInfo?.classId;
      const currentStorageKey = getStorageKey(currentUserId, currentClassId);
      const collectionName = getCollectionName(currentUser); // 회원/비회원 컬렉션 자동 선택

      // 1단계: Firestore에서 최신 데이터 가져오기 (회원/비회원 공통 로직)
      let docId;
      if (currentClassId) {
        docId = currentUser ? `${currentUserId}_${currentClassId}` : currentClassId;
      } else {
        docId = currentUserId;
      }
      const userDocRef = doc(db, collectionName, docId);
      
      const userDocResult = await safeFirestoreRead(userDocRef);
      let userData = null;
      
      if (userDocResult.success && userDocResult.doc.exists()) {
        userData = userDocResult.doc.data();
      }
      
      if (userData) {
        let firebaseQuizzes = userData.퀴즈List || [];
        
        // 데이터 정규화
        firebaseQuizzes = firebaseQuizzes.map(quiz => {
          if (!quiz.accessCode) quiz.accessCode = quiz.id;
          if (!quiz.meta) {
            quiz.meta = {
              grade: quiz.grade,
              subject: quiz.subject,
              school: quiz.school,
              mode: quiz.mode,
              topic: quiz.topic
            };
          }
          return quiz;
        });
        
        // 강제 새로고침이 아닌 경우, 로컬스토리지와 비교하여 더 최신 데이터 사용
        if (!forceRefresh) {
          const localData = localStorage.getItem(currentStorageKey);
          if (localData) {
            try {
              const localQuizzes = normalizeLocalStorageData(currentStorageKey);
              if (Array.isArray(localQuizzes)) {
                // 로컬 데이터와 Firestore 데이터를 병합
                const mergedQuizzes = [...firebaseQuizzes];
                localQuizzes.forEach(localQuiz => {
                  if (localQuiz && localQuiz.id && 
                      !mergedQuizzes.some(fbQuiz => fbQuiz && fbQuiz.id === localQuiz.id)) {
                    mergedQuizzes.push(localQuiz);
                  }
                });
                firebaseQuizzes = mergedQuizzes;
              }
            } catch (parseError) {
              // 로컬 데이터 파싱 실패, Firestore 데이터만 사용
            }
          }
        }
        
        // 최신순으로 정렬
        firebaseQuizzes.sort((a, b) => {
          const aTime = a.createdAt ? new Date(a.createdAt) : new Date(0);
          const bTime = b.createdAt ? new Date(b.createdAt) : new Date(0);
          return bTime - aTime;
        });
        
        // 항상 최종 병합된 데이터로 UI 업데이트
        setSavedQuizzes(firebaseQuizzes);
        
        // 로컬스토리지 동기화
        try {
          localStorage.setItem(currentStorageKey, JSON.stringify(firebaseQuizzes));
        } catch (storageError) {
          // 로컬 저장이 실패해도 Firestore 데이터는 UI에 표시
        }
        
        setIsLoading(false);
        setHasInitialLoad(true);
      } else {
        setSavedQuizzes([]);
        localStorage.setItem(currentStorageKey, JSON.stringify([]));
        setIsLoading(false);
        setHasInitialLoad(true);
      }
    } catch (error) {
      // 사용자에게 보여줄 에러 메시지 설정
      let errorMessage = '퀴즈 목록을 불러오는 중 오류가 발생했습니다.';
      if (error.code === 'permission-denied') {
        errorMessage = '데이터 접근 권한이 없습니다. 다시 로그인해주세요.';
      } else if (error.code === 'unavailable') {
        errorMessage = '서버에 연결할 수 없습니다. 인터넷 연결을 확인해주세요.';
      } else if (error.message?.includes('Firebase')) {
        errorMessage = 'Firebase 연결 오류가 발생했습니다. 잠시 후 다시 시도해주세요.';
      }
      
      setError(errorMessage);
      
      // 🚀 에러 시 로컬스토리지 데이터로 빠른 복구
      const errorClassId = classInfo?.classId;
      const errorStorageKey = getStorageKey(currentUserId, errorClassId);
      const localData = normalizeLocalStorageData(errorStorageKey);
      
      if (localData.length > 0) {
        setSavedQuizzes(localData);
        setIsLoading(false);
        setHasInitialLoad(true);
        return;
      }
      
      // 로컬 데이터도 없으면 빈 배열로 설정
      setSavedQuizzes([]);
      setIsLoading(false);
      setHasInitialLoad(true);
    }
  }, [currentUser, isGuest]); // classInfo 의존성 제거 - 함수 내부에서 현재 상태 참조

  // 🚀 실시간 데이터 동기화 - 페이지 포커스 및 가시성 변경 시 (TeacherTest 방식)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && userId) {
        loadQuizzes(userId, true);
      }
    };

    const handleFocus = () => {
      if (userId) {
        loadQuizzes(userId, true);
      }
    };

    // 🎯 퀴즈 저장 완료 이벤트 수신
    const handleQuizUpdated = (event) => {
      if (userId) {
        loadQuizzes(userId, true);
      }
    };

    // 이벤트 리스너 등록
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('shortform-quiz-updated', handleQuizUpdated);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('shortform-quiz-updated', handleQuizUpdated);
    };
  }, [userId]); // loadQuizzes 의존성 제거

  // 🚀 userId 설정 useEffect (분리)
  useEffect(() => {
    if (authLoading) return;

    // localStorage에서 선생님 ID 우선 확인, 없으면 currentUser.uid 또는 generateUserId() 사용
    const teacherId = storedClassData?.currentUserInfo?.uid || currentUser?.uid || generateUserId(currentUser);
    setUserId(teacherId);
  }, [authLoading, currentUser?.uid, storedClassData?.currentUserInfo?.uid]);

  // 🚀 데이터 로딩 useEffect (userId가 설정된 후)
  useEffect(() => {
    if (!userId) return;
    
    setIsLoading(true);
    loadQuizzes(userId, false);
  }, [userId]); // userId가 변경될 때만 실행

  // 🚀 퀴즈 플레이
  const handlePlayQuiz = useCallback((targetCode) => {
    if (!targetCode) {
      return;
    }

    // localStorage에서 classId가 있으면 포함해서 전달
    if (classInfo?.classId) {
      const url = `/play/shortFormQuiz/${targetCode}/teacher?classId=${classInfo.classId}`;
      navigate(url);
    } else {
      const url = `/play/shortFormQuiz/${targetCode}/teacher`;
      navigate(url);
    }
  }, [navigate, classInfo]);

  // 🚀 퀴즈 수정
  const handleEditQuiz = useCallback((quizId) => {
    // 퀴즈 수정 페이지로 이동 (ShortFormQuizModifyPage)
    navigate(`/play/shortFormQuiz/${quizId}/modify`);
  }, [navigate]);

  // 🚀 개선된 퀴즈 삭제 (TeacherTest 방식)
  const handleDeleteQuiz = useCallback(async (quiz) => {
    if (!quiz?.id || !userId) return;
    
    // 삭제하려는 퀴즈 자체에 기록된 classId를 사용 (가장 중요)
    const targetClassId = quiz.meta?.classId;
    
    // 1. 낙관적 업데이트: UI에서 먼저 즉시 제거
    const originalQuizzes = savedQuizzes;
    setSavedQuizzes(prev => prev.filter(q => q.id !== quiz.id));

    try {
      const result = await deleteShortFormQuiz({
        id: quiz.id,
        currentUser,
        classId: targetClassId
      });
      
      // 2. Firestore 삭제 실패 시 롤백 및 알림
      if (!result.success || !result.storage.firestore) {
        throw new Error('데이터베이스에서 퀴즈를 삭제하지 못했습니다.');
      }
      
      // 3. 성공 시: 아무것도 하지 않음 (이미 UI에 반영됨)
      
    } catch (error) {
      alert(error.message || '퀴즈 삭제 중 오류가 발생했습니다.');
      // 4. 실패 시: UI 롤백 (사라졌던 퀴즈를 다시 표시)
      setSavedQuizzes(originalQuizzes);
    }
  }, [userId, currentUser, savedQuizzes, loadQuizzes]);

  // 🚀 콘텐츠 생성 버튼
  const handleCreateContent = useCallback(() => {
    if (classInfo?.classId) {
      navigate(`/play/shortFormQuiz/setting?classId=${classInfo.classId}`);
    } else {
      navigate('/play/shortFormQuiz/setting');
    }
  }, [navigate, classInfo]);

  // 퀴즈 카드 렌더링 최적화
  const quizCards = useMemo(() => {
    if (!Array.isArray(savedQuizzes)) {
      return [];
    }
    return savedQuizzes.map((quiz) => (
      <QuizCard
        key={quiz.id}
        quiz={quiz}
        onPlay={() => {
          handlePlayQuiz(quiz.accessCode || quiz.id);
        }}
        onEdit={() => handleEditQuiz(quiz.id)}
        onDelete={() => handleDeleteQuiz(quiz)}
      />
    ));
  }, [savedQuizzes, handlePlayQuiz, handleEditQuiz, handleDeleteQuiz]);

  return (
    <section className="content-start-section">
      <div className="container">
        {/* 네비게이션 */}

        {/* 콘텐츠 프레임 */}
        <div className="tools-frame-content">
          {/* 헤더 */}
          <div className="section-header shortform-section-header" style={{ textAlign: 'center' }}>
            <h2 className="section-title shortform-section-title">숏폼 퀴즈를 선택하세요</h2>
          </div>

          {/* 현재 선택된 학급 정보 표시 - 오른쪽 정렬 */}
          {classInfo && (
            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              marginBottom: 'var(--spacing-md)'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--spacing-sm)',
                color: '#333333',
                fontSize: 'var(--font-md)',
                fontWeight: '500'
              }}>
                <Users size={18} />
                <span>{classInfo.className || classInfo.name} ({classStudentCount}명)</span>
              </div>
            </div>
          )}

          {/* 학급 로드 에러 표시 */}
          {classLoadError && (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              marginBottom: 'var(--spacing-md)',
              padding: 'var(--spacing-md)',
              backgroundColor: '#fef2f2',
              borderRadius: 'var(--radius-md)',
              border: '1px solid #ef4444'
            }}>
              <div style={{
                color: '#dc2626',
                fontSize: 'var(--font-sm)',
                textAlign: 'center'
              }}>
                ⚠️ {classLoadError}
              </div>
            </div>
          )}

          {/* 로딩 상태 - 초기 로딩이 완료되지 않았거나 현재 로딩 중일 때 */}
          {(isLoading || !hasInitialLoad) && (
            <div style={{ textAlign: 'center', padding: 'var(--spacing-xl)' }}>
              <div className="loading-spinner" style={{ margin: '0 auto 1rem' }}></div>
              <p style={{ color: 'var(--gray-600)' }}>퀴즈를 불러오는 중...</p>
            </div>
          )}

          {/* 에러 상태 */}
          {error && !isLoading && (
            <div style={{ 
              textAlign: 'center', 
              padding: 'var(--spacing-xl)',
              border: '1px solid var(--danger-light)',
              borderRadius: 'var(--radius-md)',
              backgroundColor: 'var(--danger-lighter)'
            }}>
              <h3 style={{ color: 'var(--danger)', marginBottom: '1rem' }}>오류가 발생했습니다</h3>
              <p style={{ color: 'var(--gray-700)', marginBottom: '1.5rem' }}>{error}</p>
              <button 
                className="btn-secondary"
                onClick={() => {
                  setError(null);
                  if (userId) {
                    setIsLoading(true);
                    loadQuizzes(userId, true);
                  }
                }}
              >
                다시 시도
              </button>
            </div>
          )}

          {/* 퀴즈 목록 또는 빈 상태 - 초기 로딩 완료 후에만 표시 */}
          {!isLoading && !error && hasInitialLoad && (
            <>
              {(!savedQuizzes || savedQuizzes.length === 0) ? (
                <div style={{ textAlign: 'center', padding: 'var(--spacing-xl)' }}>
                  <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>📝</div>
                  <h3 style={{ marginBottom: '0.5rem', color: 'var(--gray-700)' }}>아직 생성된 퀴즈가 없습니다</h3>
                  <p style={{ color: 'var(--gray-500)', marginBottom: '2rem' }}>새로운 퀴즈를 만들어보세요!</p>
                  <AddQuizCard onClick={handleCreateContent} />
                </div>
              ) : (
                <>
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    marginBottom: 'var(--spacing-lg)'
                  }}>
                    <h3 style={{ margin: 0, color: 'var(--gray-800)' }}>
                      저장된 퀴즈 ({savedQuizzes.length}개)
                    </h3>
                  </div>
                  
                  {/* 퀴즈 그리드 */}
                  <div className="quiz-grid">
                    {/* 저장된 퀴즈 카드들 */}
                    {quizCards}
                    
                    {/* 새 퀴즈 추가 카드 */}
                    <AddQuizCard onClick={handleCreateContent} />
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </section>
  );
};

export default ShortFormQuizHomePage;